import type { Dayjs } from "dayjs";

export type FilterValue = string | number | boolean | null;
export type FilterType =
  | "text"
  | "text_array"
  | "boolean"
  | "numeric"
  | "datetime"
  | "categorical"
  | "geo";

export const NUMERIC_CONDITIONS = [
  "equals",
  "not_equals",
  "greater_than",
  "less_than",
  "greater_than_or_equal",
  "less_than_or_equal",
  "has_data",
  "no_data",
  "between",
] as const;

export const DATE_CONDITIONS = [
  "equals",
  "before",
  "after",
  "between",
  "no_data",
] as const;

export const TEXT_CONDITIONS = [
  "contains",
  "equals",
  "starts_with",
  "ends_with",
  "not_contains",
] as const;

export const GEO_CONDITIONS = ["within_radius"] as const;

export type NumericCondition = (typeof NUMERIC_CONDITIONS)[number];
export type DateCondition = (typeof DATE_CONDITIONS)[number];
export type TextCondition = (typeof TEXT_CONDITIONS)[number];
export type GeoCondition = (typeof GEO_CONDITIONS)[number];

export type GeoDisplayUnit = "mi" | "km";

export type DateFilterMode = "absolute" | "relative";

export type RelativeDatePreset =
  | "today"
  | "this_week"
  | "last_week"
  | "next_30_days"
  | "last_30_days"
  | "next_90_days"
  | "last_90_days"
  | "current_quarter"
  | "ytd";

export type NumericFilterCondition = {
  condition: NumericCondition;
  value?: number;
  min?: number;
  max?: number;
};

export type DateFilterCondition = {
  condition: DateCondition;
  value?: Date | string | Dayjs;
  startDate?: Date | string | Dayjs;
  endDate?: Date | string | Dayjs;
  mode?: DateFilterMode;
  relativePreset?: RelativeDatePreset;
};

export type TextFilterCondition = {
  condition: TextCondition;
  value: string;
};

/**
 * Radius filter around a geographic point. `radius_km` is the canonical
 * stored/sent unit; `display_unit` and `label` are UI-only fields stripped
 * from the API payload (see filtersApiUtil).
 */
export type GeoFilterCondition = {
  condition: GeoCondition;
  lat: number;
  lng: number;
  radius_km: number;
  display_unit?: GeoDisplayUnit;
  /** Reverse-geocoded place name, e.g. "Boston, MA". */
  label?: string | null;
};

export type CategoricalFilterValue = FilterValue[];

export type CategoricalFilterMode = "include" | "exclude";

export type CategoricalExclusionCondition = {
  condition: "not_in";
  values: FilterValue[];
};

export type FilterConditionValue =
  | NumericFilterCondition[]
  | DateFilterCondition[]
  | TextFilterCondition[]
  | CategoricalExclusionCondition[]
  | GeoFilterCondition[]
  | CategoricalFilterValue;

export type NumericInitialValues = {
  condition: NumericCondition | null;
  value: number | null;
  min: number | null;
  max: number | null;
};

export type DateInitialValues = {
  condition: string | null;
  date: Dayjs | null;
  range: [Dayjs, Dayjs] | null;
  mode: DateFilterMode | null;
  relativePreset: RelativeDatePreset | null;
};

export type DateApplyData = {
  condition: string | null;
  date: Dayjs | null;
  range: [Dayjs, Dayjs] | null;
  mode: DateFilterMode;
  relativePreset: RelativeDatePreset | null;
};

export type SelectedFilters = Record<string, FilterValue[]>;

export type TempSelectedFilters = Record<string, FilterConditionValue>;

export type ConfirmedFilters = Record<string, FilterConditionValue>;

export type PendingDeletions = Set<string>;
